import React, { useCallback, useMemo } from 'react';

import Button from '@components/Button';
import MultiSelect from '@components/MultiSelect';

import type { DateRangeFilter } from '@containers/common/CitizenMapView/utils';

import './style.scss';

export type StatusFilter = 'OPEN' | 'CLOSED' | 'ALL';

export interface ISubmissionFilters {
  status: StatusFilter;
  categoryIds: number[];
  dateRange: DateRangeFilter;
}

interface ISubmissionFiltersProps {
  filters: ISubmissionFilters;
  mapCategories: ICRMCategoryResponse[];
  requestCountText: string;
  onChange: React.Dispatch<React.SetStateAction<ISubmissionFilters>>;
}

const STATUS_FILTERS: { label: string; value: StatusFilter }[] = [
  { label: 'Open', value: 'OPEN' },
  { label: 'Closed', value: 'CLOSED' },
  { label: 'All', value: 'ALL' },
];

const DATE_RANGE_OPTIONS: { label: string; value: DateRangeFilter }[] = [
  { label: 'Reported any time', value: 'ANY_TIME' },
  { label: 'Today', value: 'TODAY' },
  { label: 'Last 7 days', value: 'LAST_7' },
  { label: 'Last 30 days', value: 'LAST_30' },
  { label: 'Last 90 days', value: 'LAST_90' },
];

export const DEFAULT_SUBMISSION_FILTERS: ISubmissionFilters = {
  status: 'OPEN',
  categoryIds: [],
  dateRange: 'LAST_90',
};

export const CLEARED_SUBMISSION_FILTERS: ISubmissionFilters = {
  ...DEFAULT_SUBMISSION_FILTERS,
  status: 'ALL',
};

export function hasActiveSubmissionFilters(filters: ISubmissionFilters) {
  return (
    filters.status !== 'ALL' ||
    filters.categoryIds.length > 0 ||
    (filters.dateRange !== 'LAST_90' && filters.dateRange !== 'ANY_TIME')
  );
}

export default function SubmissionFilters({
  filters,
  mapCategories,
  requestCountText,
  onChange,
}: ISubmissionFiltersProps) {
  const categoryOptions = useMemo(
    () => mapCategories.map((category) => ({ value: category.id, label: category.name })),
    [mapCategories]
  );
  const hasActiveFilters = hasActiveSubmissionFilters(filters);

  const updateFilters = useCallback(
    (nextFilters: Partial<ISubmissionFilters>) => {
      onChange((currentFilters) => ({ ...currentFilters, ...nextFilters }));
    },
    [onChange]
  );

  const handleClearFilters = useCallback(() => {
    onChange(CLEARED_SUBMISSION_FILTERS);
  }, [onChange]);

  return (
    <>
      <span className="citizen-map-view__top-bar-count">{requestCountText}</span>
      <div className="citizen-map-view__top-bar-filters" role="group" aria-label="Filter by status">
        {STATUS_FILTERS.map(({ label, value }) => (
          <Button
            key={value}
            bsStyle={filters.status === value ? ButtonStyle.PRIMARY : ButtonStyle.DEFAULT}
            content={label}
            onClick={() => updateFilters({ status: value })}
            aria-pressed={filters.status === value}
          />
        ))}
      </div>
      {mapCategories.length > 0 && (
        <div className="citizen-map-view__top-bar-category" role="group" aria-label="Filter by category">
          <MultiSelect
            name="categoryFilter"
            value={filters.categoryIds}
            options={categoryOptions}
            onChange={(e: React.SyntheticEvent<HTMLInputElement> & { target: { value: number[] } }) =>
              updateFilters({ categoryIds: Array.isArray(e.target?.value) ? e.target.value : [] })
            }
            placeholder="Category"
            ariaLabelForListbox="Filter by category"
          />
        </div>
      )}
      <div className="citizen-map-view__top-bar-date" role="group" aria-label="Filter by date">
        <span className="citizen-map-view__date-select-wrapper">
          <select
            value={filters.dateRange}
            onChange={(e) => updateFilters({ dateRange: e.target.value as DateRangeFilter })}
            aria-label="Date range"
            className="citizen-map-view__date-select"
          >
            {DATE_RANGE_OPTIONS.map(({ value, label }) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
          <span className="citizen-map-view__date-select-icon" aria-hidden="true">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="1" y="2" width="14" height="13" rx="1" stroke="currentColor" strokeWidth="1.2" />
              <path d="M1 5.5h14" stroke="currentColor" strokeWidth="1.2" />
              <path d="M4.5 1v3M11.5 1v3" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
            </svg>
          </span>
        </span>
      </div>
      <button
        type="button"
        className="citizen-map-view__clear-filters"
        onClick={handleClearFilters}
        aria-label="Clear all filters"
        disabled={!hasActiveFilters}
      >
        <span className="citizen-map-view__clear-filters-icon" aria-hidden="true">
          ×
        </span>
        <span>Clear Filters</span>
      </button>
    </>
  );
}
